/**
 * @description
 * Deployment details page showing comprehensive information about a specific deployment
 * including health monitoring, logs, and management actions.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import { ArrowLeft, RefreshCw, RotateCcw, Trash2, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../services/api';
import { DeploymentHealthMonitoring } from '../components/deployment/DeploymentHealthMonitoring';
import { useDeploymentLogs } from '../hooks/useDeploymentLogs';

interface DeploymentDetailsData {
  id: string;
  clientId: string;
  version: string;
  status: 'pending' | 'deploying' | 'success' | 'failed' | 'rolled_back';
  deployedBy: string;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  logs: string;
  progress: string;
  clientName?: string;
  subdomain?: string;
}

interface HealthCheck {
  worker: {
    status: string;
  };
}

export function DeploymentDetails() {
  const { id } = useParams<{ id: string }>();
  const [deployment, setDeployment] = useState<DeploymentDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'health' | 'logs'>('overview');
  const [healthStatus, setHealthStatus] = useState<HealthCheck | null>(null);

  const fetchDeploymentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await api.getDeploymentDetails(id!) as DeploymentDetailsData;
      setDeployment(details);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch deployment details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchDeploymentDetails();
    }
  }, [id, fetchDeploymentDetails]);


  const handleStart = async () => {
    toast('Starting local deployment...');
    try {
      await api.startLocalDeployment(deployment!.clientId);
      toast.success('Local deployment started.');
      fetchDeploymentDetails();
    } catch {
      toast.error('Failed to start local deployment.');
    }
  };

  const handleStop = async () => {
    toast('Stopping local deployment...');
    try {
      await api.stopLocalDeployment(deployment!.clientId);
      toast.success('Local deployment stopped.');
      fetchDeploymentDetails();
    } catch {
      toast.error('Failed to stop local deployment.');
    }
  };

  const handleRollback = async () => {
    if (!id) return;
    
    try {
      await api.rollbackDeployment(id);
      toast.success('Deployment rollback initiated');
      fetchDeploymentDetails();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rollback deployment');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    if (!confirm('Are you sure you want to delete this deployment? This action cannot be undone.')) {
      return;
    }

    try {
      await api.deleteDeployment(id);
      toast.success('Deployment deleted successfully');
      // Redirect to deployments list
      window.location.href = '/deployments';
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete deployment');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'deploying':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rolled_back':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Status icon helper (currently unused but kept for future use)
  /*
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'failed':
        return '❌';
      case 'deploying':
        return '🔄';
      case 'pending':
        return '⏳';
      case 'rolled_back':
        return '↩️';
      default:
        return '❓';
    }
  };
  */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error || !deployment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/deployments" className="text-primary hover:underline">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <h1 className="text-3xl font-bold">Deployment Details</h1>
        </div>
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error || 'Deployment not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/deployments" className="text-primary hover:underline">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Deployment Details</h1>
            <p className="text-muted-foreground">
              {deployment.clientName} - Version {deployment.version}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={fetchDeploymentDetails}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          
          {deployment.status === 'success' && (
            <button
              onClick={handleRollback}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
            >
              <RotateCcw className="w-4 h-4" />
              Rollback
            </button>
          )}
          
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold">Local Development Status</h3>
          </div>
          {healthStatus ? (
            <div>
              <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(healthStatus.worker.status)}`}>
                {healthStatus.worker.status}
              </span>
              <div className="mt-4 flex gap-2">
                <button onClick={handleStart} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50" disabled={healthStatus.worker.status === 'running'}>Start</button>
                <button onClick={handleStop} className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50" disabled={healthStatus.worker.status !== 'running'}>Stop</button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">Loading...</p>
          )}
        </div>
        
        <div className="p-4 bg-white border rounded-lg">
          <h3 className="font-semibold mb-2">Deployed By</h3>
          <p className="text-sm text-gray-600">{deployment.deployedBy}</p>
        </div>
        
        <div className="p-4 bg-white border rounded-lg">
          <h3 className="font-semibold mb-2">Started At</h3>
          <p className="text-sm text-gray-600">{new Date(deployment.startedAt).toLocaleString()}</p>
        </div>
        
        <div className="p-4 bg-white border rounded-lg">
          <h3 className="font-semibold mb-2">Completed At</h3>
          <p className="text-sm text-gray-600">
            {deployment.completedAt ? new Date(deployment.completedAt).toLocaleString() : 'In Progress'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('health')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'health'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Health Monitoring
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logs'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Logs
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Client Information */}
          <div className="p-6 bg-white border rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Client Name</label>
                <p className="text-sm">{deployment.clientName || 'Unknown'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Subdomain</label>
                <p className="text-sm">{deployment.subdomain || 'Unknown'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Client ID</label>
                <p className="text-sm font-mono">{deployment.clientId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Version</label>
                <p className="text-sm font-mono">{deployment.version}</p>
              </div>
            </div>
          </div>

          {/* Error Information */}
          {deployment.errorMessage && (
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-lg font-semibold mb-4 text-red-800">Error Details</h3>
              <pre className="text-sm text-red-700 whitespace-pre-wrap">{deployment.errorMessage}</pre>
            </div>
          )}
        </div>
      )}

      {activeTab === 'health' && (
        <DeploymentHealthMonitoring deploymentId={id!} onHealthUpdate={setHealthStatus} />
      )}

      {activeTab === 'logs' && <LogsTab deployment={deployment} />}
    </div>
  );
}

/**
 * Logs tab component with real-time streaming
 */
function LogsTab({ deployment }: { deployment: DeploymentDetailsData }) {
  const { logs: realtimeLogs, connected, error: wsError } = useDeploymentLogs(deployment?.clientId || null);
  const [showRealtime, setShowRealtime] = useState(true);

  return (
    <div className="p-6 bg-white border rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Deployment Logs</h3>
          
          {/* Real-time toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showRealtime}
              onChange={(e) => setShowRealtime(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Real-time</span>
          </label>

          {/* Connection status */}
          {showRealtime && (
            <>
              {connected && (
                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></span>
                  Live
                </span>
              )}
              {wsError && (
                <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                  {wsError}
                </span>
              )}
              {!connected && !wsError && (
                <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                  Connecting...
                </span>
              )}
            </>
          )}
        </div>
        
        <button className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700">
          <Download className="w-4 h-4" />
          Download Logs
        </button>
      </div>

      {/* Log display */}
      <div className="bg-gray-900 text-gray-100 p-4 rounded overflow-auto max-h-96 font-mono text-sm">
        {showRealtime ? (
          <>
            {realtimeLogs.length > 0 ? (
              realtimeLogs.map((log, i) => (
                <div
                  key={i}
                  className={`${
                    log.level === 'stderr' || log.level === 'error' ? 'text-red-400' :
                    log.level === 'warn' ? 'text-yellow-400' :
                    'text-gray-300'
                  }`}
                >
                  <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
                  <span className="text-gray-600">[{log.level}]</span>{' '}
                  {log.message}
                </div>
              ))
            ) : (
              <div className="text-gray-500">
                {connected
                  ? 'Waiting for logs... Start a deployment to see real-time output.'
                  : 'Connecting to log stream...'}
              </div>
            )}
          </>
        ) : (
          <div className="text-gray-300 whitespace-pre-wrap">
            {deployment.logs || 'No historical logs available'}
          </div>
        )}
      </div>

      {/* Log info */}
      <div className="mt-2 text-xs text-gray-500">
        {showRealtime ? (
          <p>Showing real-time logs from process-manager service. Toggle off to view historical logs.</p>
        ) : (
          <p>Showing historical logs from database. Toggle on to view real-time logs.</p>
        )}
      </div>
    </div>
  );
}

export default DeploymentDetails;
